import { Button, Skeleton, TextField, Typography } from '@compass/components'
import { Kbd } from '@compass/Kbd'
import { Menu, menuItemStyles, MenuTrigger } from '@compass/Menu/Menu'
import { Popover } from '@compass/Popover'
import { Tooltip } from '@compass/Tooltip'
import { cn } from '@compass/utils'
import { useLocation } from '@tanstack/react-router'
import { AppRoute } from 'appRoutes'
import { ComponentErrorBoundary, PersistentDateRangePicker, useDefaultPredefinedRanges } from 'features/commonUI'
import { useCurrentSubscriptionData } from 'features/subscription'
import {
  DEFAULT_STORED_DATA_TTL_DAYS,
  EVENTS_DATE_RANGE_IDS,
  filterRangesWithinTtl,
  getEarliestAllowedDate,
  getMaxRangeDays,
  isPredefinedRange,
} from 'helpers/dateRange'
import { useLogPageView } from 'helpers/vendor'
import { Check, ChevronRight, ListFilterPlus } from 'lucide-react'
import { DateTime, Duration } from 'luxon'
import {
  VisitsFilterKey,
  VisitsFilterKeysWithoutDate,
  VisitsFilterListItem,
  VisitsFilterListItemChecklist,
  VisitsFilterListItemRadiolist,
} from 'models'
import { ampli } from 'models/ampli'
import {
  KeyboardEvent as ReactKeyboardEvent,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { Placement } from 'react-aria'
import {
  Autocomplete,
  Focusable,
  Header,
  MenuItem,
  MenuSection,
  Selection,
  Separator,
  SubmenuTrigger,
  TooltipTrigger,
  useFilter,
} from 'react-aria-components'

import { useFilterSearchParams } from '../../hooks/useFilterSearchParams'
import { useVisitsFilter } from '../../hooks/useVisitsFilter'
import { IdentificationUrlKeys } from '../../types'
import { VisitHistoryFilterItem } from '../VisitHistoryFilterItem/VisitHistoryFilterItem'

type FilterState = 'closed' | 'menu' | 'detail'

export interface VisitHistoryFilterProps {
  timezone?: string
  subscriptionId: string
  appliedFilterCount: number
}

export function VisitHistoryFilter({ timezone, subscriptionId, appliedFilterCount }: VisitHistoryFilterProps) {
  const { filterItems, filterMenuLayout, disabledFilterKeys } = useVisitsFilter()
  const { filterValues, setSearchParams } = useFilterSearchParams()
  const { ranges } = useDefaultPredefinedRanges()
  const { contains } = useFilter({ sensitivity: 'base' })
  const { subscription } = useCurrentSubscriptionData()
  const storedDataTtlDays = subscription?.storedDataTtl ?? DEFAULT_STORED_DATA_TTL_DAYS
  const MAX_RANGE_DAYS = getMaxRangeDays(storedDataTtlDays)

  const triggerRef = useRef<HTMLButtonElement | null>(null)

  const [filterValue, setFilterValue] = useState('')
  const [selected] = useState<Selection>(new Set(['filter-input', filterMenuLayout[0].items[0].id]))
  const [filterState, setFilterState] = useState<FilterState>('closed')
  const [selectedFilterItemId, setSelectedFilterItemId] = useState<VisitsFilterKey | null>(null)

  const definedRanges = useMemo(() => {
    return filterRangesWithinTtl(
      Object.values(ranges).filter((range) => EVENTS_DATE_RANGE_IDS.includes(range.id)),
      storedDataTtlDays,
      timezone
    )
  }, [ranges, storedDataTtlDays, timezone])

  const filteredSearchItems = useMemo(() => {
    if (!filterValue) {
      return []
    }

    const items: Array<
      | {
          type: 'child'
          key: string
          textValue: string
          parentLabel: string
          child: VisitsFilterListItem
          parentSectionId: string
        }
      | {
          type: 'option'
          key: string
          textValue: string
          grandParentLabel: string
          parentLabel: string
          option: { id: string; name: string; icon?: ReactNode }
          filter: VisitsFilterListItemChecklist | VisitsFilterListItemRadiolist
        }
      | { type: 'top-level'; filterItem: VisitsFilterListItem }
    > = []
    for (const section of filterMenuLayout) {
      for (const filterItem of section.items) {
        if ('children' in filterItem) {
          for (const submenuSection of filterItem.children) {
            for (const child of submenuSection.items) {
              const combinedLabel = `${filterItem.label} ${child.label}`

              if (contains(combinedLabel, filterValue)) {
                items.push({
                  type: 'child',
                  key: submenuSection.id + child.id,
                  textValue: combinedLabel,
                  parentLabel: filterItem.label,
                  child: child as VisitsFilterListItem,
                  parentSectionId: submenuSection.id,
                })
                continue
              }

              const searchableChild = child as VisitsFilterListItem & { searchableOptions?: boolean }
              const hasSearchableOptions =
                'inputType' in searchableChild &&
                (searchableChild.inputType === 'checklist' || searchableChild.inputType === 'radiolist') &&
                searchableChild.searchableOptions

              if (!hasSearchableOptions) {
                continue
              }

              for (const option of (searchableChild as VisitsFilterListItemChecklist | VisitsFilterListItemRadiolist)
                .options) {
                if (!contains(`${combinedLabel} ${option.name}`, filterValue)) {
                  continue
                }

                items.push({
                  type: 'option',
                  key: `${submenuSection.id}-${searchableChild.id}-${option.id}`,
                  textValue: `${combinedLabel} ${option.name}`,
                  grandParentLabel: filterItem.label,
                  parentLabel: child.label,
                  option,
                  filter: searchableChild as VisitsFilterListItemChecklist | VisitsFilterListItemRadiolist,
                })
              }
            }
          }
        } else if (contains(filterItem.label, filterValue)) {
          items.push({ type: 'top-level', filterItem })
        }
      }
    }
    return items
  }, [filterValue, filterMenuLayout, contains])

  const hasSearchResults = filteredSearchItems.length > 0

  const handleFilterInputChange = (value: string) => {
    setFilterValue(value.trimStart())
  }

  const handleOpenMenu = useCallback(() => {
    setFilterState('menu')
    ampli.attributeFilterDialogOpened({ datasetFocus: 'Identification Events' })
  }, [])

  const handleCloseMenu = useCallback(() => {
    setFilterState('closed')
    setTimeout(() => {
      setFilterValue('')
      setSelectedFilterItemId(null)
    }, 150)
  }, [])

  const handleMenuItemAction = useCallback(
    ({
      menuItem,
      parentSectionId,
    }: {
      menuItem:
        | VisitsFilterListItem
        | {
            id: string
            label: string
            icon: ReactNode
            tooltip: string
          }
      parentSectionId?: string
    }) => {
      if ('inputType' in menuItem) {
        if (menuItem.inputType === 'boolean') {
          setFilterState('closed')
          setSearchParams({ [menuItem.id]: true })
        } else {
          setSelectedFilterItemId(menuItem.id as VisitsFilterKey)
          setFilterState('detail')
        }
      } else {
        if (parentSectionId) {
          if (parentSectionId === IdentificationUrlKeys.SdkPlatform) {
            ampli.platformFilterApplied({
              datasetFocus: 'Identification Events',
              'App Page Title': document.title,
              'App Page URL': window.location.href,
              'App Page Path': AppRoute.IdentificationEvents,
              platformName: [menuItem.label],
            })
          }

          setSearchParams({ [parentSectionId]: menuItem.id })
        }
      }
    },
    [setSearchParams]
  )

  const handleOptionAction = useCallback(
    (filter: VisitsFilterListItemChecklist | VisitsFilterListItemRadiolist, option: { id: string }) => {
      if (filter.inputType === 'checklist') {
        const currentValues = (filterValues[filter.id] as string[] | undefined) ?? []
        const nextValues = currentValues.includes(option.id)
          ? currentValues.filter((value) => value !== option.id)
          : [...currentValues, option.id]

        setSearchParams({ [filter.id]: nextValues.length > 0 ? nextValues : undefined })
      } else {
        setSearchParams({ [filter.id]: option.id })
      }

      setFilterState('closed')
    },
    [filterValues, setSearchParams]
  )

  const handleCloseDetail = useCallback(() => {
    setFilterState('closed')
    setTimeout(() => {
      setSelectedFilterItemId(null)
    }, 150)
  }, [])

  const handleFilterInputKeyDown = useCallback(
    (evt: ReactKeyboardEvent<HTMLInputElement>) => {
      if (evt.code === 'Escape') {
        evt.preventDefault()
        handleCloseMenu()
      }
      if (evt.code === 'Enter') {
        evt.preventDefault()
      }
    },
    [handleCloseMenu]
  )

  // Global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyPress = (evt: KeyboardEvent) => {
      const hasModifier = evt.metaKey || evt.ctrlKey || evt.altKey || evt.shiftKey

      if (evt.code === 'Escape') {
        if (filterState === 'detail') {
          handleCloseDetail()
        } else if (filterState === 'menu') {
          handleCloseMenu()
        }
      }

      // Open the filter menu only if an input is not focused and no dialog is open
      if (
        evt.code === 'KeyF' &&
        !hasModifier &&
        document.activeElement?.tagName !== 'INPUT' &&
        document.activeElement?.tagName !== 'TEXTAREA' &&
        !(document.activeElement as HTMLElement)?.isContentEditable &&
        !document.activeElement?.closest('[role="dialog"]')
      ) {
        evt.preventDefault()
        handleOpenMenu()
      }
    }

    window.addEventListener('keydown', handleGlobalKeyPress)
    return () => window.removeEventListener('keydown', handleGlobalKeyPress)
  }, [filterState, handleCloseDetail, handleCloseMenu, handleOpenMenu])

  useEffect(() => {
    if (filterState === 'closed') {
      setTimeout(() => {
        setFilterValue('')
      }, 150)
    }
  }, [filterState])

  useTrackInitialFilterParams()

  return (
    <ComponentErrorBoundary>
      {/* This is needed for focus control */}
      <TooltipTrigger>
        <MenuTrigger
          isOpen={filterState === 'menu'}
          onOpenChange={(o: boolean) => {
            setFilterState((fs) => {
              if (fs === 'detail') {
                return fs
              }
              return o ? 'menu' : 'closed'
            })
          }}
        >
          <Button
            name='Filter'
            ref={triggerRef}
            onPress={handleOpenMenu}
            variant='secondary'
            tooltip={
              <>
                Filters <Kbd className='ml-1'>F</Kbd>
              </>
            }
            data-testid='visit-history-filter-trigger'
          >
            <ListFilterPlus />
            Filters
            {appliedFilterCount > 0 ? (
              <Typography className='text-orange-700 tabular-nums'>{appliedFilterCount}</Typography>
            ) : null}
          </Button>
          <Popover
            placement='bottom left'
            triggerRef={triggerRef}
            data-testid='visit-history-filter-popover'
            className={cn('min-w-60 outline-none', filterState === 'detail' && 'duration-0')}
          >
            <Autocomplete onInputChange={handleFilterInputChange} inputValue={filterValue}>
              <TextField
                aria-label='Filter menu by keyword'
                id='filter-input'
                autoFocus
                size='lg'
                classes={{
                  input: 'placeholder-gray-800',
                  wrapper:
                    'border-none rounded-lg shadow-none focus-within:ring-0 outline-none focus-within:shadow-none focus:bg-transparent',
                }}
                placeholder='Filter...'
                suffix={<Kbd>F</Kbd>}
                onKeyDown={handleFilterInputKeyDown}
              />
              <Separator />
              <Menu
                selectionMode='multiple'
                selectedKeys={selected}
                className='p-0'
                renderEmptyState={() => (
                  <Typography size='sm' className='h-11 flex items-center justify-center text-gray-800'>
                    No matches
                  </Typography>
                )}
              >
                {filterValue.length > 0 ? (
                  hasSearchResults ? (
                    <MenuSection className='p-1 border-b last:border-none'>
                      {filteredSearchItems.map((item) => {
                        if (item.type === 'child') {
                          return (
                            <MenuItem
                              key={item.key}
                              id={item.key}
                              textValue={item.textValue}
                              className={(renderProps) =>
                                cn(
                                  menuItemStyles({ ...renderProps, isSelected: false }),
                                  'relative gap-2 justify-start'
                                )
                              }
                              onAction={() => {
                                handleMenuItemAction({ menuItem: item.child, parentSectionId: item.parentSectionId })
                              }}
                              data-testid='history-filter-menu-item'
                            >
                              {item.child.icon}
                              <span className='flex items-center gap-1 font-medium'>
                                <span className='text-gray-800'>{item.parentLabel}</span>
                                <ChevronRight size={14} className='stroke-gray-700' />
                                {item.child.label}
                              </span>
                            </MenuItem>
                          )
                        }
                        if (item.type === 'option') {
                          const currentValue = filterValues[item.filter.id]
                          const isSelected =
                            item.filter.inputType === 'checklist'
                              ? ((currentValue as string[] | undefined)?.includes(item.option.id) ?? false)
                              : currentValue === item.option.id

                          return (
                            <MenuItem
                              key={item.key}
                              id={item.key}
                              textValue={item.textValue}
                              className={(renderProps) =>
                                cn(
                                  menuItemStyles({ ...renderProps, isSelected: false }),
                                  'relative gap-2 justify-start'
                                )
                              }
                              onAction={() => handleOptionAction(item.filter, item.option)}
                              data-testid='history-filter-menu-item'
                            >
                              {item.option.icon}
                              <span className='flex items-center gap-1 font-medium'>
                                <span className='text-gray-800'>{item.grandParentLabel}</span>
                                <ChevronRight size={14} className='stroke-gray-700' />
                                <span className='text-gray-800'>{item.parentLabel}</span>
                                <ChevronRight size={14} className='stroke-gray-700' />
                                {item.option.name}
                              </span>
                              {isSelected && <Check size={16} aria-hidden className='flex-shrink-0 ml-auto' />}
                            </MenuItem>
                          )
                        }
                        return (
                          <MenuItem
                            key={item.filterItem.id}
                            id={item.filterItem.id}
                            textValue={item.filterItem.label}
                            className={(renderProps) =>
                              cn(menuItemStyles({ ...renderProps, isSelected: false }), 'relative gap-2 justify-start')
                            }
                            onAction={() => handleMenuItemAction({ menuItem: item.filterItem })}
                          >
                            {item.filterItem.icon}
                            <span className='font-medium'>{item.filterItem.label}</span>
                            {Object.keys(filterValues).includes(item.filterItem.id) && (
                              <Check size={16} aria-hidden className='flex-shrink-0 ml-auto' />
                            )}
                          </MenuItem>
                        )
                      })}
                    </MenuSection>
                  ) : null
                ) : (
                  filterMenuLayout.map((section) => (
                    <MenuSection key={section.id} className='p-1 border-b last:border-none'>
                      {section.items.map((filterItem) => {
                        if (filterItem && 'children' in filterItem) {
                          return (
                            <SubmenuTrigger key={filterItem.id} delay={0}>
                              <MenuItem
                                key={filterItem.id}
                                textValue={filterItem.label}
                                id={filterItem.id}
                                className={(renderProps) =>
                                  cn(
                                    menuItemStyles({ ...renderProps, isSelected: false }),
                                    'relative gap-2 justify-start'
                                  )
                                }
                                data-testid='history-filter-menu-item'
                              >
                                {filterItem.icon}
                                <span className='font-medium'>{filterItem.label}</span>
                                <ChevronRight size={16} className='ml-auto' />
                              </MenuItem>
                              <Popover
                                showArrow={false}
                                placement='right top'
                                offset={0}
                                className='min-w-52 outline-none shadow-sub-popover'
                              >
                                <Menu className='p-0'>
                                  {filterItem.children.map((submenuSection) => (
                                    <MenuSection key={submenuSection.id} className='p-1 border-b last:border-none'>
                                      {'label' in submenuSection && (
                                        <Header className='px-2 py-1 select-none text-2xs font-medium tracking-wide uppercase text-gray-700 truncate'>
                                          {submenuSection.label}
                                        </Header>
                                      )}
                                      {submenuSection.items.map((child) => {
                                        const isFilterDisabled =
                                          'inputType' in child && disabledFilterKeys.has(child.id)
                                        return (
                                          <MenuItem
                                            key={submenuSection.id + child.id}
                                            textValue={child.label}
                                            id={submenuSection.id + child.id}
                                            isDisabled={isFilterDisabled}
                                            className={(renderProps) =>
                                              cn(
                                                menuItemStyles({ ...renderProps, isSelected: false }),
                                                'relative gap-2 justify-start',
                                                isFilterDisabled && 'opacity-50 cursor-not-allowed'
                                              )
                                            }
                                            onAction={() => {
                                              if (!isFilterDisabled) {
                                                handleMenuItemAction({
                                                  menuItem: child,
                                                  parentSectionId: submenuSection.id,
                                                })
                                              }
                                            }}
                                            data-testid='history-filter-submenu-item'
                                          >
                                            {child.icon}
                                            <span className='font-medium'>{child.label}</span>
                                            {('inputType' in child
                                              ? Object.keys(filterValues).includes(child.id)
                                              : filterValues[submenuSection.id as VisitsFilterKeysWithoutDate] ===
                                                child.id) && (
                                              <Check size={16} aria-hidden className='flex-shrink-0 ml-auto' />
                                            )}
                                            <MaybeAddTooltip
                                              tooltip={
                                                isFilterDisabled
                                                  ? 'Not included in your plan. Please visit /plans to request an Enterprise trial.'
                                                  : child.tooltip
                                              }
                                              placement='right'
                                              className='text-left'
                                            >
                                              <Focusable
                                                onKeyDown={(event) => {
                                                  if (event.key === 'Enter' && !isFilterDisabled) {
                                                    event.preventDefault()
                                                    handleMenuItemAction({
                                                      menuItem: child,
                                                      parentSectionId: submenuSection.id,
                                                    })
                                                  }
                                                }}
                                              >
                                                <span
                                                  tabIndex={-1}
                                                  role='button'
                                                  className='absolute'
                                                  style={{
                                                    /* TODO: replace with '-inset-y-2 focus-visible:inset-0' after upgrading to Tailwind v4 */
                                                    top: '-0.5rem',
                                                    bottom: '-0.5rem',
                                                    left: '0',
                                                    right: '0',
                                                  }}
                                                />
                                              </Focusable>
                                            </MaybeAddTooltip>
                                          </MenuItem>
                                        )
                                      })}
                                    </MenuSection>
                                  ))}
                                </Menu>
                              </Popover>
                            </SubmenuTrigger>
                          )
                        }
                        return (
                          <MenuItem
                            key={filterItem.id}
                            textValue={filterItem.label}
                            id={filterItem.id}
                            className={(renderProps) =>
                              cn(menuItemStyles({ ...renderProps, isSelected: false }), 'relative gap-2 justify-start')
                            }
                            onAction={() => {
                              handleMenuItemAction({ menuItem: filterItem })
                            }}
                            data-testid='history-filter-menu-item'
                          >
                            {filterItem.icon}
                            <span className='font-medium'>{filterItem.label}</span>
                            {Object.keys(filterValues).includes(filterItem.id) && (
                              <Check size={16} aria-hidden className='shrink-0 ml-auto' />
                            )}
                            <MaybeAddTooltip tooltip={filterItem.tooltip} placement='right' className='text-left'>
                              <Focusable
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    event.preventDefault()
                                    handleMenuItemAction({ menuItem: filterItem })
                                  }
                                }}
                              >
                                <span
                                  tabIndex={-1}
                                  role='button'
                                  className='absolute'
                                  style={{
                                    /* TODO: replace with '-inset-y-2 focus-visible:inset-0' after upgrading to Tailwind v4 */
                                    top: '-0.5rem',
                                    bottom: '-0.5rem',
                                    left: '0',
                                    right: '0',
                                  }}
                                />
                              </Focusable>
                            </MaybeAddTooltip>
                          </MenuItem>
                        )
                      })}
                    </MenuSection>
                  ))
                )}
              </Menu>
            </Autocomplete>
          </Popover>
        </MenuTrigger>
      </TooltipTrigger>

      {selectedFilterItemId && (
        <Popover
          isOpen={filterState === 'detail'}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              handleCloseDetail()
            }
          }}
          triggerRef={triggerRef}
          placement='bottom start'
          showArrow
          className={`min-w-64 outline-none ${filterState === 'detail' && 'duration-0'}`}
        >
          <VisitHistoryFilterItem
            key={filterItems[selectedFilterItemId].id}
            {...filterItems[selectedFilterItemId]}
            setIsOpen={(isOpen) => setFilterState(isOpen ? 'detail' : 'closed')}
            isOpen={filterState === 'detail'}
            autoFocus
          />
        </Popover>
      )}

      {timezone && subscription ? (
        <PersistentDateRangePicker
          timezone={timezone}
          route={AppRoute.IdentificationEvents}
          routeParams={{ subscriptionId }}
          definedRanges={definedRanges}
          defaultDateRange={ranges.today}
          initialMinDate={getEarliestAllowedDate(storedDataTtlDays, timezone)}
          maxRange={Duration.fromDurationLike({ days: MAX_RANGE_DAYS })}
          onOpen={() => ampli.dateFilterSelectorOpened({ datasetFocus: 'Identification Events' })}
          onApplyRange={(range) => {
            ampli.dateFilterApplied({
              datasetFocus: 'Identification Events',
              dateRange: isPredefinedRange(range) ? (range.label as any) : 'Other',
              periodLengthDays: Math.round(range.endDate.diff(range.startDate, 'days').days),
            })
          }}
        />
      ) : (
        <Skeleton className='w-56 h-8' />
      )}
    </ComponentErrorBoundary>
  )
}

interface MaybeAddTooltipProps extends PropsWithChildren {
  tooltip?: ReactNode
  placement?: Placement
  className?: string
}
const MaybeAddTooltip = ({ tooltip, children, placement = 'left', className }: MaybeAddTooltipProps) => {
  if (!tooltip) {
    return children
  }

  return (
    <TooltipTrigger delay={1000} closeDelay={0}>
      {children}
      <Tooltip placement={placement} offset={16} className={cn('max-w-64 duration-0', className)}>
        {tooltip}
      </Tooltip>
    </TooltipTrigger>
  )
}

function useTrackInitialFilterParams() {
  const search = useLocation({ select: (l) => l.searchStr })
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const searchOnLoad = useMemo(() => search, [])

  useLogPageView(() => {
    const searchParams = new URLSearchParams(searchOnLoad)

    const sinceParam = searchParams.get(IdentificationUrlKeys.Since)
    const beforeParam = searchParams.get(IdentificationUrlKeys.Before)
    const initialStartDate = sinceParam ? new Date(sinceParam) : DateTime.now().startOf('day').toJSDate()
    const initialEndDate = beforeParam ? new Date(beforeParam) : DateTime.now().endOf('day').toJSDate()
    const values = {
      'IP address': searchParams.has(IdentificationUrlKeys.IpAddress),
      URL: searchParams.has(IdentificationUrlKeys.Url),
      linkedID: searchParams.has(IdentificationUrlKeys.LinkedId),
      requestID: searchParams.has(IdentificationUrlKeys.RequestId),
      visitorID: searchParams.has(IdentificationUrlKeys.VisitorId),
      origin: searchParams.has(IdentificationUrlKeys.Origin),
      environment: searchParams.has(IdentificationUrlKeys.Environment),
    }
    const setFilters = Object.entries(values)
      .filter(([_, value]) => value === true)
      .map(([key]) => key as keyof typeof values)

    if (setFilters.length > 0) {
      ampli.dataWithFiltersAppliedViewed({
        datasetFocus: 'Identification Events',
        attributeFilters: setFilters,
        periodLengthDays: Math.round(
          DateTime.fromJSDate(initialEndDate).diff(DateTime.fromJSDate(initialStartDate), 'days').days
        ),
      })
    }
  })
}
